import { lazy, type ComponentType, type LazyExoticComponent } from 'react'

const RETRY_PREFIX = 'lazy-retry:'

function getRetryStorageKey(retryKey: string): string {
  return `${RETRY_PREFIX}${retryKey}`
}

type ChunkLoadPhase =
  /** 読込に失敗したので、1回だけ読み直す */
  | 'retrying'
  /** 読み直したら通った */
  | 'recovered'
  /** 読み直しても通らなかったので、エラー画面に渡す */
  | 'gave-up'

/**
 * chunk 読込の結末を記録する。
 *
 * デプロイで chunk のハッシュが変わると、開いたままのタブは消えた chunk を
 * 要求する。_redirects の SPA フォールバックにより、それには index.html が
 * 200 で返るので "Importing a module script failed" になる。1回の読み直しで
 * 復帰するはずだが、BT でそうならない事例があり、原因が未特定のまま残って
 * いる。再発したときに、1回目で諦めたのか読み直しても駄目だったのかを
 * 区別できるようにする（PRI-251）。
 *
 * utils/logger を使わないのは、あれが本番で ERROR しか出さないため。
 */
function recordChunkLoad(
  phase: ChunkLoadPhase,
  retryKey: string,
  error?: unknown,
): void {
  console.info(
    JSON.stringify({
      timestamp: new Date().toISOString(),
      channel: 'chunk-load',
      phase,
      retryKey,
      message: error instanceof Error ? error.message : undefined,
    }),
  )
}

export function isRecoverableChunkError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '')
  const name = error instanceof Error ? error.name : ''
  const normalized = `${name} ${message}`.toLowerCase()

  return (
    normalized.includes('failed to fetch dynamically imported module') ||
    normalized.includes('importing a module script failed') ||
    normalized.includes('chunkloaderror') ||
    normalized.includes('error loading dynamically imported module')
  )
}

export const lazyWithRetryRuntime = {
  reloadPage(): void {
    window.location.reload()
  },
}

export async function loadModuleWithRetry<T>(
  loader: () => Promise<T>,
  retryKey: string,
): Promise<T> {
  try {
    const module = await loader()
    if (typeof window !== 'undefined') {
      const storageKey = getRetryStorageKey(retryKey)
      if (window.sessionStorage.getItem(storageKey) === '1') {
        recordChunkLoad('recovered', retryKey)
      }
      window.sessionStorage.removeItem(storageKey)
    }
    return module
  } catch (error) {
    if (
      typeof window === 'undefined' ||
      !isRecoverableChunkError(error)
    ) {
      throw error
    }

    const storageKey = getRetryStorageKey(retryKey)
    const hasRetried = window.sessionStorage.getItem(storageKey) === '1'

    if (!hasRetried) {
      window.sessionStorage.setItem(storageKey, '1')
      recordChunkLoad('retrying', retryKey, error)
      lazyWithRetryRuntime.reloadPage()
      return new Promise<T>(() => undefined)
    }

    recordChunkLoad('gave-up', retryKey, error)
    throw error
  }
}

export function lazyWithRetry<T extends ComponentType<any>>(
  loader: () => Promise<{ default: T }>,
  retryKey: string,
): LazyExoticComponent<T> {
  return lazy(() => loadModuleWithRetry(loader, retryKey))
}
